import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { publicSiteUrl, canNativeShare, nativeShare, copyToClipboard } from "@/lib/share";
import { toast } from "sonner";
import {
  Building2, MapPin, Globe, Mail, Phone, BadgeCheck, ShieldCheck, Users,
  Loader2, ArrowLeft, Share2, Briefcase, Wrench, Network,
} from "lucide-react";

const TYPE_ICON = Building2;

function ShareButton({ url, title }) {
  const onShare = async () => {
    if (canNativeShare()) { const r = await nativeShare({ url, title }); if (r !== "unsupported") return; }
    const ok = await copyToClipboard(url);
    toast(ok ? "Link copied." : "Copy failed");
  };
  return (
    <button type="button" onClick={onShare} data-testid="org-share"
      className="inline-flex items-center gap-1.5 rounded-xl border border-[#0F2C59]/25 bg-white px-3.5 py-2 text-sm font-semibold text-[#0F2C59] hover:bg-[#0F2C59]/5 transition-colors">
      <Share2 className="h-4 w-4" /> Share
    </button>
  );
}

function OrgBadges({ org }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="org-badges">
      <span className="inline-flex items-center gap-1 rounded-full bg-[#0F2C59] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
        {org.type_label}
      </span>
      {org.is_verified && (
        <span data-testid="org-verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
          <BadgeCheck className="h-3.5 w-3.5" /> Verified Organization
        </span>
      )}
      {org.is_partner && (
        <span data-testid="org-partner-badge" className="inline-flex items-center gap-1 rounded-full bg-[#C79A3B]/12 px-2.5 py-0.5 text-[11px] font-semibold text-[#8a6a1f] ring-1 ring-[#C79A3B]/30">
          <ShieldCheck className="h-3.5 w-3.5" /> PropertyConnect Partner
        </span>
      )}
    </div>
  );
}

function MemberCard({ m }) {
  const inner = (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 hover:shadow-md transition-shadow" data-testid={`org-member-${m.id || m.public_id}`}>
      {m.avatar_url ? (
        <img src={m.avatar_url} alt="" className="h-11 w-11 rounded-full object-cover bg-slate-100 shrink-0" />
      ) : (
        <span className="h-11 w-11 rounded-full bg-[#0F2C59]/10 text-[#0F2C59] font-semibold flex items-center justify-center shrink-0 text-sm">
          {(m.name || "P").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-semibold text-[#0F2C59] truncate">{m.name}</span>
          {m.verified && <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />}
        </div>
        <div className="text-[13px] text-slate-500 truncate">
          {[m.role_in_org === "OWNER" ? "Owner" : m.role_in_org === "ADMIN" ? "Admin" : null, m.role_label].filter(Boolean).join(" · ") || "Member"}
        </div>
      </div>
    </div>
  );
  return m.public_profile_path ? (
    <Link to={m.public_profile_path} className="block">{inner}</Link>
  ) : inner;
}

export default function PublicOrganization() {
  const { slug } = useParams();
  const [org, setOrg] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    let alive = true;
    setOrg(undefined);
    api.get(`/public/organizations/${slug}`)
      .then(({ data }) => alive && setOrg(data))
      .catch(() => alive && setOrg(null));
    return () => { alive = false; };
  }, [slug]);

  if (org === undefined) {
    return (
      <div className="min-h-screen bg-white"><PublicHeader />
        <div className="flex items-center justify-center py-32 text-slate-400" data-testid="org-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </div>
    );
  }

  if (org === null) {
    return (
      <div className="min-h-screen bg-white flex flex-col"><PublicHeader />
        <Seo robots="noindex,follow" />
        <div className="flex-1 flex flex-col items-center justify-center py-32 px-6 text-center" data-testid="org-not-found">
          <Building2 className="h-10 w-10 text-slate-300 mb-3" />
          <h1 className="font-display text-2xl font-semibold text-[#0F2C59]">Organization not available</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-md">This organization profile is unavailable or has not been published.</p>
          <Link to="/marketplace" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to Marketplace</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: org.name,
    url: `${publicSiteUrl()}/org/${org.slug}`,
    ...(org.logo ? { logo: org.logo } : {}),
    ...(org.description ? { description: org.description } : {}),
    ...(org.website ? { sameAs: [org.website] } : {}),
  };
  const shareUrl = `${publicSiteUrl()}/org/${org.slug}`;

  return (
    <div className="min-h-screen bg-white" data-testid="public-organization">
      <Seo title={`${org.name} — ${org.type_label} | PropertyConnect`} description={org.description || `${org.name} on PropertyConnect`} canonical={`/org/${org.slug}`} jsonLd={jsonLd} />
      <PublicHeader />

      {/* Cover */}
      <div className="relative h-44 sm:h-60 w-full bg-gradient-to-br from-[#0F2C59] to-[#16407f] overflow-hidden">
        {org.cover && <img src={org.cover} alt="" className="h-full w-full object-cover opacity-90" />}
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
        {/* Identity header */}
        <div className="-mt-12 sm:-mt-14 relative flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl bg-white ring-4 ring-white shadow-md flex items-center justify-center overflow-hidden shrink-0">
            {org.logo ? <img src={org.logo} alt={org.name} className="h-full w-full object-contain" /> : <TYPE_ICON className="h-10 w-10 text-[#0F2C59]" />}
          </div>
          <div className="min-w-0 flex-1 pb-1">
            <h1 data-testid="org-name" className="font-display text-2xl sm:text-3xl font-bold text-[#0F2C59] truncate">{org.name}</h1>
            <div className="mt-2"><OrgBadges org={org} /></div>
            {org.parent && (
              <div data-testid="org-parent-context" className="mt-2 flex items-center gap-1.5 text-[13px] text-slate-500">
                <Network className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span>
                  {org.organization_unit_type === "CHAPTER" ? "Chapter of " : org.organization_unit_type === "MEMBER_BOARD" ? "Member Board of " : "Part of "}
                  <Link to={`/org/${org.parent.slug}`} data-testid="org-parent-link" className="font-semibold text-[#0F2C59] hover:underline">{org.parent.name}</Link>
                </span>
              </div>
            )}
          </div>
          <div className="pb-1"><ShareButton url={shareUrl} title={org.name} /></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8 pb-16">
          {/* About */}
          <div className="lg:col-span-2 space-y-6">
            {org.description && (
              <section data-testid="org-about" className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="text-lg font-semibold text-[#0F2C59] mb-2">About</h2>
                <p className="text-[15px] leading-relaxed text-slate-600 whitespace-pre-line">{org.description}</p>
              </section>
            )}

            {(org.services || []).length > 0 && (
              <section data-testid="org-services" className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0F2C59] mb-3"><Wrench className="h-5 w-5" /> Key services</h2>
                <div className="flex flex-wrap gap-2">
                  {org.services.map((s) => (
                    <span key={s.key} data-testid={`org-service-${s.key}`} className="rounded-full bg-[#0F2C59]/8 px-3 py-1 text-sm font-medium text-[#0F2C59]">{s.label}</span>
                  ))}
                </div>
              </section>
            )}

            <section data-testid="org-members" className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0F2C59] mb-3"><Users className="h-5 w-5" /> Affiliated professionals <span className="text-sm font-normal text-slate-400">({org.member_count})</span></h2>
              {(org.members || []).length === 0 ? (
                <p className="text-sm text-slate-500">No affiliated professionals listed yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {org.members.map((m) => <MemberCard key={m.public_id || m.id || m.name} m={m} />)}
                </div>
              )}
            </section>

            {(org.children || []).length > 0 && (
              <section data-testid="org-children" className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0F2C59] mb-3"><Network className="h-5 w-5" /> Chapters &amp; Member Organizations <span className="text-sm font-normal text-slate-400">({org.children.length})</span></h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {org.children.map((c) => (
                    <Link key={c.slug} to={`/org/${c.slug}`} data-testid={`org-child-${c.slug}`} className="flex items-start gap-3 rounded-2xl border border-slate-200 p-3.5 hover:shadow-md transition-shadow">
                      <span className="h-10 w-10 rounded-xl bg-[#0F2C59]/8 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-[#0F2C59]" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[#0F2C59] text-sm truncate">{c.name}</div>
                        <div className="mt-0.5 text-[12px] text-slate-500 truncate">{[c.unit_type_label, c.type_label].filter(Boolean).join(" · ")}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {(org.recruitment || []).length > 0 && (
              <section data-testid="org-recruitment" className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0F2C59] mb-3"><Briefcase className="h-5 w-5" /> Open opportunities <span className="text-sm font-normal text-slate-400">({org.recruitment.length})</span></h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {org.recruitment.map((r) => (
                    <Link key={r.id} to={`/recruitment/${r.id}`} data-testid={`org-recruitment-${r.id}`} className="flex items-start gap-3 rounded-2xl border border-slate-200 p-3.5 hover:shadow-md transition-shadow">
                      <span className="h-10 w-10 rounded-xl bg-[#0F2C59]/8 flex items-center justify-center overflow-hidden shrink-0">
                        {r.cover ? <img src={r.cover} alt="" className="h-full w-full object-cover" /> : <Briefcase className="h-5 w-5 text-[#0F2C59]" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[#0F2C59] text-sm truncate">{r.title}</div>
                        <div className="mt-0.5 text-[12px] text-slate-500 truncate">
                          {[r.employment_type_label, r.is_remote ? "Remote" : [r.city, r.province].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Contact / meta */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="org-contact">
              {(org.city || org.province) && (
                <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{[org.city, org.province].filter(Boolean).join(", ")}</span></div>
              )}
              {org.website && (
                <a href={org.website} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 text-sm text-[#0F2C59] hover:underline"><Globe className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{org.website.replace(/^https?:\/\//, "")}</span></a>
              )}
              {org.contact_email && (
                <a href={`mailto:${org.contact_email}`} className="flex items-center gap-2 text-sm text-[#0F2C59] hover:underline"><Mail className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{org.contact_email}</span></a>
              )}
              {org.contact_mobile && (
                <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{org.contact_mobile}</span></div>
              )}
              {!(org.city || org.province || org.website || org.contact_email || org.contact_mobile) && (
                <p className="text-sm text-slate-400">No public contact details provided.</p>
              )}
            </div>
            {(org.service_areas || []).length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="org-service-areas">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Service areas</p>
                <div className="flex flex-wrap gap-1.5">
                  {org.service_areas.map((a) => <span key={a} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">{a}</span>)}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
